import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { ROUTES } from '../../../shared/config/routes';
import { createClient } from '../services/clientService';

const validationSchema = Yup.object({
  name: Yup.string().required('Campo obligatorio'),
  surname: Yup.string().required('Campo obligatorio'),
  secondSurname: Yup.string(),
  email: Yup.string().email('Email inválido').required('Campo obligatorio'),
  phone: Yup.string().required('Campo obligatorio').matches(/^\+?[0-9]{7,15}$/, 'Teléfono no válido'),
  sex: Yup.string().required('Selecciona una opción'),
  birthday: Yup.date().required('Campo obligatorio').max(new Date(), 'La fecha no puede ser futura'),
  conditionAcepted: Yup.bool().oneOf([true], 'Debes aceptar las condiciones'),
  minors: Yup.array().of(
    Yup.object({
      name: Yup.string().required('Nombre obligatorio'),
      surname: Yup.string().required('Primer apellido obligatorio'),
      secondSurname: Yup.string(),
      birthDate: Yup.date().required('Fecha obligatoria'),
    })
  ).min(1, 'Debes añadir al menos un menor a cargo'),
});

export default function RegisterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const redirectTimeoutRef = useRef(null);

  useEffect(() => () => {
    if (redirectTimeoutRef.current) {
      window.clearTimeout(redirectTimeoutRef.current);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-tr from-blue-50 via-white to-slate-50 px-4 pb-10 pt-28 md:px-6 md:pb-14 md:pt-32">
      <motion.div
        initial={{ opacity: 0, y: -40 }}
        animate={isRedirecting ? { opacity: 0, y: -18, scale: 0.985 } : { opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: isRedirecting ? 0.45 : 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto w-full max-w-5xl rounded-[32px] border border-white/70 bg-white/95 p-6 shadow-[0_30px_80px_rgba(59,130,246,0.10)] backdrop-blur-xl md:p-10"
      >
        <div className="mx-auto mb-8 max-w-3xl text-center">
          <p className="text-[11px] uppercase tracking-[0.34em] text-blue-400">Registro público</p>
          <h2 className="mt-3 text-4xl font-extrabold tracking-[-0.05em] text-blue-700 md:text-6xl">Registro de cliente</h2>
          <p className="mt-4 text-base text-slate-500 md:text-lg">
            Completa los datos del tutor y de los menores para dejar el alta registrada correctamente.
          </p>
        </div>

        <Formik
          initialValues={{
            name: '',
            surname: '',
            secondSurname: '',
            email: '',
            phone: '',
            sex: '',
            birthday: '',
            conditionAcepted: false,
            minors: [{ name: '', surname: '', secondSurname: '', birthDate: '' }],
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            setError(null);
            setSuccess(null);
            setLoading(true);

            try {
              await createClient(values);
              setSuccess('Registrado correctamente');
              setIsRedirecting(true);
              redirectTimeoutRef.current = window.setTimeout(() => navigate(ROUTES.home, {
                state: { registrationSuccess: 'Registrado correctamente' },
              }), 500);
            } catch (requestError) {
              setError('Error al registrar cliente');
            } finally {
              setLoading(false);
              setSubmitting(false);
            }
          }}
        >
          {({ values, errors, touched }) => (
            <Form className="space-y-6">
              {error && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-center text-sm font-medium text-red-600">
                  {error}
                </div>
              )}
              {success && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-center"
                >
                  <p className="text-base font-semibold text-emerald-700">Registrado correctamente</p>
                  <p className="mt-1 text-sm text-emerald-600">Redirigiendo al inicio...</p>
                </motion.div>
              )}

              <div className="grid gap-4 md:grid-cols-3">
                <div className="w-full">
                  <label className="block font-semibold text-gray-700">Nombre</label>
                  <Field name="name" type="text" className="w-full px-4 py-2 border rounded-xl" />
                  {touched.name && errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>
                <div className="w-full">
                  <label className="block font-semibold text-gray-700">Primer apellido</label>
                  <Field name="surname" type="text" className="w-full px-4 py-2 border rounded-xl" />
                  {touched.surname && errors.surname && <p className="text-red-500 text-sm mt-1">{errors.surname}</p>}
                </div>
                <div className="w-full">
                  <label className="block font-semibold text-gray-700">Segundo apellido</label>
                  <Field name="secondSurname" type="text" className="w-full px-4 py-2 border rounded-xl" />
                  {touched.secondSurname && errors.secondSurname && <p className="text-red-500 text-sm mt-1">{errors.secondSurname}</p>}
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4">
                <div className="w-full">
                  <label className="block font-semibold text-gray-700">Email</label>
                  <Field name="email" type="email" className="w-full px-4 py-2 border rounded-xl" />
                  {touched.email && errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
                <div className="w-full">
                  <label className="block font-semibold text-gray-700">Teléfono</label>
                  <Field name="phone" type="text" className="w-full px-4 py-2 border rounded-xl" />
                  {touched.phone && errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4">
                <div className="w-full">
                  <label className="block font-semibold text-gray-700">Sexo</label>
                  <Field as="select" name="sex" className="w-full px-4 py-2 border rounded-xl bg-white">
                    <option value="">Selecciona</option>
                    <option value="Hombre">Hombre</option>
                    <option value="Mujer">Mujer</option>
                    <option value="Otro">Otro</option>
                  </Field>
                  {touched.sex && errors.sex && <p className="text-red-500 text-sm mt-1">{errors.sex}</p>}
                </div>
                <div className="w-full">
                  <label className="block font-semibold text-gray-700">Fecha de nacimiento</label>
                  <Field name="birthday" type="date" className="w-full px-4 py-2 border rounded-xl" />
                  {touched.birthday && errors.birthday && <p className="text-red-500 text-sm mt-1">{errors.birthday}</p>}
                </div>
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-2">Menores a cargo</label>
                <FieldArray name="minors">
                  {({ remove, push }) => (
                    <div className="space-y-4">
                      {values.minors.map((minor, index) => (
                        <div key={index} className="grid md:grid-cols-2 gap-4 border p-4 rounded-xl bg-gray-50">
                          <div>
                            <Field name={`minors[${index}].name`} placeholder="Nombre" className="w-full px-4 py-2 border rounded-lg" />
                            {touched.minors?.[index]?.name && errors.minors?.[index]?.name && (
                              <p className="text-red-500 text-sm">{errors.minors[index].name}</p>
                            )}
                          </div>
                          <div>
                            <Field name={`minors[${index}].surname`} placeholder="Primer apellido" className="w-full px-4 py-2 border rounded-lg" />
                            {touched.minors?.[index]?.surname && errors.minors?.[index]?.surname && (
                              <p className="text-red-500 text-sm">{errors.minors[index].surname}</p>
                            )}
                          </div>
                          <div>
                            <Field name={`minors[${index}].secondSurname`} placeholder="Segundo apellido" className="w-full px-4 py-2 border rounded-lg" />
                            {touched.minors?.[index]?.secondSurname && errors.minors?.[index]?.secondSurname && (
                              <p className="text-red-500 text-sm">{errors.minors[index].secondSurname}</p>
                            )}
                          </div>
                          <div>
                            <Field name={`minors[${index}].birthDate`} type="date" className="w-full px-4 py-2 border rounded-lg" />
                            {touched.minors?.[index]?.birthDate && errors.minors?.[index]?.birthDate && (
                              <p className="text-red-500 text-sm">{errors.minors[index].birthDate}</p>
                            )}
                          </div>
                          <button type="button" onClick={() => remove(index)} className="text-red-500 text-sm mt-2 md:col-span-2">Eliminar</button>
                        </div>
                      ))}

                      <button type="button" onClick={() => push({ name: '', surname: '', secondSurname: '', birthDate: '' })} className="mt-2 rounded-xl bg-blue-500 px-4 py-2 text-white transition hover:bg-blue-600">
                        Añadir menor
                      </button>
                      {typeof errors.minors === 'string' && touched.minors && (
                        <p className="text-red-500 text-sm mt-2">{errors.minors}</p>
                      )}
                    </div>
                  )}
                </FieldArray>
              </div>

              <div className="flex items-start space-x-2">
                <Field name="conditionAcepted" type="checkbox" className="mt-1" />
                <label className="text-sm text-gray-700">
                  Acepto las <Link to={ROUTES.terms} className="text-blue-600 underline">condiciones legales</Link>
                </label>
              </div>
              {touched.conditionAcepted && errors.conditionAcepted && (
                <p className="text-red-500 text-sm">{errors.conditionAcepted}</p>
              )}

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-600">
                <p className="font-semibold text-slate-800">Información básica sobre protección de datos</p>
                <p className="mt-2">
                  Responsable: 77 SUAN XI S.L. Finalidad: gestionar el registro de la persona adulta responsable y de los menores a su cargo para la organización, control de acceso y seguridad en las instalaciones. Legitimación: ejecución de la relación de servicio y cumplimiento de obligaciones legales. Derechos: acceso, rectificación, supresión, oposición, limitación y demás derechos reconocidos por la normativa, mediante solicitud a <span className="font-medium">informundolandia@gmail.com</span>.
                </p>
                <p className="mt-2">
                  Información ampliada en nuestra <Link to={ROUTES.privacyPolicy} className="text-blue-600 underline">Política de privacidad</Link>.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-blue-600 py-3 text-lg font-semibold text-white transition duration-300 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? 'Registrando...' : success ? 'Redirigiendo...' : 'Registrarse'}
              </button>
            </Form>
          )}
        </Formik>
      </motion.div>
    </div>
  );
}
